import CoreBase from '../../../../../services/engines/CoreBase';

import { registerFocusCommand } from './registerFocusCommand';
import { registerTabCommand } from './registerTabCommand';
import { registerClockCommand } from './registerClockCommand';
import { registerStopwatchCommand } from './registerStopwatchCommand';
import { registerCountdownCommand } from './registerCountdownCommand';
import { registerNoteCommand } from './registerNoteCommand';
import { registerAppBarCommand } from './registerAppBarCommand';
import { registerCalEventCommand } from './registerCalEventCommand';
import { registerTickerCommand } from './registerTickerCommand';
import { registerUmlCommand } from './registerUmlCommand';
import { registerCmcCommand } from './registerCmcCommand';
import { registerRiskProfileCommand } from './registerRiskProfileCommand';
import { registerScenarioAssetCommand } from './registerScenarioAssetCommand';
import { registerUserAssetCommand } from './registerUserAssetCommand';
import { registerConfidenceCommand } from './registerConfidenceCommand';
import { registerAnalysisCommand } from './registerAnalysisCommand';
import { registerSimResultCommand } from './registerSimResultCommand';
import { registerFileListerCommand } from './registerFileListerCommand';
import { registerPaperTraderCommand } from './registerPaperTraderCommand';
import { registerPnl3Command } from './registerPnl3Command';
import { registerCardCommand } from './registerCardCommand';
import { registerP1MeterCommand } from './registerP1MeterCommand';
import { registerTibberPriceCommand } from './registerTibberPriceCommand';
import { registerServiceOverviewCommand } from './registerServiceOverviewCommand';
import { registerBuildOverviewCommand } from './registerBuildOverviewCommand';
import { registerBatteryCommand } from './registerBatteryCommand';
import { registerBatteryOverviewCommand } from './registerBatteryOverviewCommand';
import { registerBatSeqCommand } from './registerBatSeqCommand';
import { registerScheduleOverviewCommand } from './registerScheduleOverviewCommand';
import { registerPowerUsageCommand } from './registerPowerUsageCommand';
import { registerPowerUsageMonthCommand } from './registerPowerUsageMonthCommand';
import { registerPlugVueCommand } from './registerPlugVueCommand';
import { registerBoardCommand } from './registerBoardCommand';
import { registerWorkspaceCommand } from './registerWorkspaceCommand';
import { registerOutputCommand } from './registerOutputCommand';

/**
 * CommandEngine — singleton registry for slash commands.
 * Replaces the plain registerCommand/parseAndExecute functions in CommandProcessor.
 *
 * Each command has metadata for hint display in CmdBarEdit:
 *   cmd    — e.g. '/focus'
 *   hint   — short description shown in the dropdown, e.g. 'Toggle focus mode'
 *   params — ordered list of expected parameters, each with name + hint
 *            e.g. [{ name: 'mode', hint: 'on | off' }]
 *   handler — called with string[] of parsed params on execute
 */
class CommandEngine extends CoreBase {
    // todo: this should be commandEngineCache
    _commands = new Map(); // cmd string → { hint, params, handler }

    /**
     * register commands
     */
    bootup = () => {
        registerFocusCommand();
        registerCalEventCommand();
        registerTabCommand();
        registerClockCommand();
        registerStopwatchCommand();
        registerCountdownCommand();
        registerNoteCommand();
        registerAppBarCommand();
        registerTickerCommand();
        registerUmlCommand();
        registerCmcCommand();
        registerRiskProfileCommand();
        registerScenarioAssetCommand();
        registerUserAssetCommand();
        registerConfidenceCommand();
        registerAnalysisCommand();
        registerSimResultCommand();
        registerFileListerCommand();
        registerPaperTraderCommand();
        registerPnl3Command();
        registerCardCommand();
        registerP1MeterCommand();
        registerTibberPriceCommand();
        registerServiceOverviewCommand();
        registerBuildOverviewCommand();
        registerBatteryCommand();
        registerBatteryOverviewCommand();
        registerBatSeqCommand();
        registerScheduleOverviewCommand();
        registerPowerUsageCommand();
        registerPowerUsageMonthCommand();
        registerPlugVueCommand();
        registerBoardCommand();
        registerWorkspaceCommand();
        registerOutputCommand();
    };

    /**
     * @param {string} cmd - slash command, e.g. '/focus'
     * @param {{ hint: string, params: { name: string, hint: string }[], handler: function }} def
     */
    register(cmd, def) {
        this._commands.set(cmd, def);
    }

    /**
     * Returns commands whose key starts with the given prefix, sorted on cmd.
     * Used by CmdBarEdit to populate the dropdown.
     * @param {string} prefix - e.g. '/fo'
     * @returns {{ cmd: string, hint: string, params: object[] }[]}
     */
    getMatches(prefix) {
        const result = [];
        for (const [cmd, def] of this._commands) {
            if (cmd.startsWith(prefix)) {
                result.push({ cmd, hint: def.hint, params: def.params ?? [] });
            }
        }

        result.sort((a, b) => a.cmd.localeCompare(b.cmd));
        return result;
    }

    /** @returns {number} all registered commands */
    count() {
        return this._commands.size;
    }

    /**
     * Returns the full definition for an exact command, or null.
     * @param {string} cmd
     */
    getCommand(cmd) {
        return this._commands.get(cmd) ?? null;
    }

    /**
     * Parses input, finds a matching command, calls its handler.
     * @param {string} input - raw value from CmdBarEdit
     * @returns {boolean} true if a command was matched and executed
     */
    execute(input) {
        const [cmd, ...params] = input.trim().split(/\s+/);
        const def = this._commands.get(cmd);
        if (!def) return false;
        def.handler(params);
        return true;
    }
}

const commandEngine = new CommandEngine();
export default commandEngine;
